/**
 * Project Execution Center (PEC) — Iter-110a
 * "Site Brain" · Construction Evidence System (CES)
 *
 * Single-page command center per project. URL: /project-execution/:projectId
 * If projectId missing, shows a project picker first.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Header: project name, client, KPIs (contract value, billed)  │
 *   │ Phase progress bar (8 phases, colour-coded)                   │
 *   ├──────────────────────────────────────┬────────────────────────┤
 *   │ Tabs:                                │ Sidebar Dashboard      │
 *   │  • Site Readiness                    │  workers today         │
 *   │  • Areas (recursive tree)            │  daily/cumulative      │
 *   │  • Daily Execution Log               │  MAR/MIR open          │
 *   │  • MAR Upload                        │  billing-ready value   │
 *   │  • MIR Upload                        │  VO count              │
 *   │  • Materials (consumption summary)   │  Alerts feed           │
 *   │  • Mobilization (read-only summary)  │                        │
 *   │  • Billing Pipeline (read-only)      │                        │
 *   │  • VO (link)                         │                        │
 *   │  • Closeout (link)                   │                        │
 *   └──────────────────────────────────────┴────────────────────────┘
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import QuickActions from "../components/pec/QuickActions";
import {
  Building, ChevronRight, ChevronDown, Plus, Upload, RefreshCw,
  CheckCircle2, Clock, AlertTriangle, FileText, Camera, MapPin,
  HardHat, ClipboardCheck, FileCheck2, Calendar, Boxes, Wallet,
  GitBranch, Lock, Image as ImageIcon, X, Save, Eye, FolderArchive,
  Activity, Users, ListChecks, Trash2,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

const PHASE_DEFS = [
  { key: "site_readiness",   label: "استلام الموقع",   icon: MapPin },
  { key: "mobilization",     label: "التجهيز",         icon: HardHat },
  { key: "material_receipt", label: "MAR استلام المواد", icon: Boxes },
  { key: "daily_execution",  label: "التنفيذ اليومي",  icon: Calendar },
  { key: "inspection",       label: "MIR الفحص",       icon: ClipboardCheck },
  { key: "billing",          label: "الفوترة",         icon: Wallet },
  { key: "vo",               label: "VO أوامر التغيير", icon: GitBranch },
  { key: "closeout",         label: "الإغلاق",         icon: Lock },
];

const PHASE_TONES = {
  done:        { dot: "bg-emerald-500", ring: "ring-emerald-200", text: "text-emerald-700" },
  in_progress: { dot: "bg-amber-500",   ring: "ring-amber-200",   text: "text-amber-700"   },
  issue:       { dot: "bg-rose-500",    ring: "ring-rose-200",    text: "text-rose-700"    },
  todo:        { dot: "bg-slate-300",   ring: "ring-slate-200",   text: "text-slate-500"   },
};

const MAR_STATUSES = ["Draft", "Submitted", "Under Review", "Approved", "Rejected"];
const MIR_STATUSES = ["Pending", "Inspected", "Approved", "Rework", "Closed"];

// ── Tabs definitions ───────────────────────────────────────────────
const TABS = [
  { key: "site_readiness", label: "استلام الموقع",   icon: MapPin },
  { key: "areas",          label: "Areas المناطق",   icon: Building },
  { key: "boq",            label: "BOQ",             icon: ListChecks },
  { key: "contract",       label: "Contract",        icon: FileText },
  { key: "daily",          label: "التنفيذ اليومي",  icon: Calendar },
  { key: "mar",            label: "MAR",             icon: Boxes },
  { key: "mir",            label: "MIR",             icon: ClipboardCheck },
  { key: "materials",      label: "Materials",       icon: FolderArchive },
  { key: "mobilization",   label: "Mobilization",    icon: HardHat },
  { key: "billing",        label: "Billing Pipeline", icon: Wallet },
  { key: "vo",             label: "VO",              icon: GitBranch },
  { key: "closeout",       label: "Closeout",        icon: Lock },
];

// AreaNode component lifted to module scope to avoid no-unstable-nested-components.
function AreaNode({ a, depth, byParent, expanded, setExpanded, del }) {
  const kids = byParent[a.id] || [];
  const open = expanded[a.id] ?? (depth < 2);
  return (
    <div data-testid={`area-node-${a.id}`} style={{ paddingRight: depth * 16 }}>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-2.5 my-1">
        <div className="flex items-center gap-2">
          {kids.length > 0 ? (
            <button onClick={() => setExpanded(e => ({ ...e, [a.id]: !open }))} className="text-slate-400 hover:text-slate-700">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : <span className="w-4" />}
          <Building className="h-4 w-4 text-violet-500" />
          <span className="text-sm font-semibold text-slate-900">{a.name}</span>
          <span className="text-[11px] text-slate-500">· {a.code}</span>
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">{a.area_type}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span title="الجاهزية">جاهزية: <b className="text-slate-900">{a.readiness_pct || 0}%</b></span>
          <span title="الوزن">وزن: <b className="text-slate-900">{a.weight_pct || 0}%</b></span>
          <button onClick={() => del(a.id)} className="text-rose-500 hover:text-rose-700"><X className="h-4 w-4" /></button>
        </div>
      </div>
      {open && kids.map(k => (
        <AreaNode key={k.id} a={k} depth={depth + 1} byParent={byParent} expanded={expanded} setExpanded={setExpanded} del={del} />
      ))}
    </div>
  );
}

function PhotoStripStatic({ label, urls, onAdd, testid }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold text-slate-600">{label} ({urls.length})</div>
      <div className="flex flex-wrap gap-2">
        {urls.map((u, i) => (
          <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200">
            <ImageIcon className="h-4 w-4 text-slate-400" />
          </a>
        ))}
        <label data-testid={testid} className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-400">
          <Camera className="h-4 w-4 text-slate-400" />
          <input type="file" accept="image/*" hidden onChange={onAdd} />
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PROJECT PICKER (when no projectId in URL)
// ─────────────────────────────────────────────────────────────────
function ProjectPicker() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get("/pec/projects/list");
        if (alive) setItems(r.data.items || []);
      } catch (e) { toast.error("تعذّر تحميل المشاريع"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="pec-project-picker" className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <Building className="h-6 w-6 text-violet-600" />
        <h1 className="text-2xl font-bold text-slate-900">Project Execution Center</h1>
      </div>
      <p className="mb-6 text-sm text-slate-500">Site Brain · Construction Evidence System — اختر مشروعاً لفتح مركز قيادته.</p>
      {loading ? (
        <div className="text-center text-slate-500 py-12">جارٍ تحميل المشاريع…</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <button
              key={p.id}
              data-testid={`pec-pick-${p.id}`}
              onClick={() => navigate(`/project-execution/${p.id}`)}
              className="group rounded-xl border border-slate-200 bg-white p-4 text-right shadow-sm transition-all hover:-translate-y-0.5 hover:border-violet-400 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <Building className="h-5 w-5 text-violet-500" />
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase text-slate-600">{p.status}</span>
              </div>
              <div className="mt-3 text-base font-bold text-slate-900">{p.name}</div>
              <div className="mt-1 text-xs text-slate-500">{p.client_name || "—"}</div>
              <div className="mt-2 text-sm font-semibold text-violet-700">{fmt(p.budget)} SAR</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PHASE BAR
// ─────────────────────────────────────────────────────────────────
function PhaseBar({ phases }) {
  return (
    <div data-testid="pec-phase-bar" className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 min-w-max">
        {PHASE_DEFS.map((p, idx) => {
          const status = phases?.[p.key] || "todo";
          const t = PHASE_TONES[status];
          const Icon = p.icon;
          return (
            <React.Fragment key={p.key}>
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ${t.ring}`}>
                <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                <Icon className={`h-4 w-4 ${t.text}`} />
                <span className={`text-xs font-semibold ${t.text}`}>{p.label}</span>
              </div>
              {idx < PHASE_DEFS.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SIDEBAR DASHBOARD
// ─────────────────────────────────────────────────────────────────
function DashboardSidebar({ data, onTabClick, tasks = [], onTaskAction }) {
  if (!data) return null;
  return (
    <aside data-testid="pec-dashboard-sidebar" className="space-y-3">
      <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">Today</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.today?.workers || 0}</div>
            <div className="text-[10px] text-slate-500">عمال اليوم</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.cumulative_30d?.log_days || 0}</div>
            <div className="text-[10px] text-slate-500">يوم سجل (30d)</div>
          </div>
        </div>
      </div>

      <SidebarStatRow icon={Boxes}          label="MAR مفتوحة"           value={`${data.mar?.open || 0} / ${data.mar?.total || 0}`} onClick={() => onTabClick("mar")} tone="amber" />
      <SidebarStatRow icon={ClipboardCheck} label="MIR مفتوحة"           value={data.mir?.open || 0} sub={`${data.mir?.approved || 0} مُعتمدة`} onClick={() => onTabClick("mir")} tone="indigo" />
      <SidebarStatRow icon={Wallet}         label="جاهز للفوترة"          value={`${fmt(data.billing_ready_value || 0)} SAR`} onClick={() => onTabClick("billing")} tone="emerald" />
      <SidebarStatRow icon={GitBranch}      label="أوامر التغيير VO"      value={data.vo_count || 0} onClick={() => onTabClick("vo")} tone="violet" />
      <SidebarStatRow icon={Building}       label="عدد المناطق"           value={data.area_count || 0} onClick={() => onTabClick("areas")} tone="sky" />


      {/* Tasks Inbox (Iter-111: Approval → notify accounting/manager) */}
      {tasks.length > 0 && (
        <div data-testid="pec-tasks-inbox" className="rounded-xl border border-violet-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold text-violet-700">
            <Activity className="h-4 w-4" /> مهام مفتوحة ({tasks.length})
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {tasks.slice(0, 8).map(t => (
              <div key={t.id} data-testid={`pec-task-${t.id}`} className="rounded-md border border-slate-100 bg-slate-50 p-2 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${t.assigned_to_role === "accountant" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {t.assigned_to_role}
                  </span>
                  <button data-testid={`pec-task-done-${t.id}`} onClick={() => onTaskAction?.(t, "done")} className="text-emerald-600 hover:underline text-[10px]">إنهاء ✓</button>
                </div>
                <div className="mt-1 font-semibold text-slate-800">{t.title}</div>
                <div className="text-slate-500">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.alerts || []).length > 0 && (
        <div data-testid="pec-alerts" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold text-amber-800">
            <AlertTriangle className="h-4 w-4" /> تحذيرات
          </div>
          <div className="space-y-2">
            {data.alerts.map((a, i) => (
              <div key={i} className={`rounded-md p-2 text-[11px] ${a.severity === "warn" ? "bg-amber-100 text-amber-900" : a.severity === "danger" ? "bg-rose-100 text-rose-900" : "bg-sky-100 text-sky-900"}`}>
                {a.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

function SidebarStatRow({ icon: Icon, label, value, sub, onClick, tone = "violet" }) {
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-right transition-all hover:border-violet-300 hover:shadow-sm"
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-${tone}-100 text-${tone}-600`}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-sm font-bold text-slate-900">{value}</div>
          {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// FILE UPLOAD UTILITY
// ─────────────────────────────────────────────────────────────────
async function uploadFile(projectId, kind, file) {
  const fd = new FormData();
  fd.append("project_id", projectId);
  fd.append("kind", kind);
  fd.append("file", file);
  const r = await axiosInstance.post("/pec/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
}

// ─────────────────────────────────────────────────────────────────
// TAB: SITE READINESS
// ─────────────────────────────────────────────────────────────────
function SiteReadinessTab({ projectId, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState({ handover_date: "", ready_areas: "", not_ready_areas: "", blockers: "", notes: "", photo_urls: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axiosInstance.get(`/pec/site-readiness?project_id=${projectId}`);
    setDoc(r.data && r.data.id ? r.data : null);
    if (r.data && r.data.id) {
      setDraft({
        handover_date: r.data.handover_date || "",
        ready_areas: (r.data.ready_areas || []).join("، "),
        not_ready_areas: (r.data.not_ready_areas || []).join("، "),
        blockers: (r.data.blockers || []).join("، "),
        notes: r.data.notes || "",
        photo_urls: r.data.photo_urls || [],
      });
    }
  }, [projectId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/site-readiness?project_id=${projectId}`);
        if (!alive) return;
        setDoc(r.data && r.data.id ? r.data : null);
        if (r.data && r.data.id) {
          setDraft({
            handover_date: r.data.handover_date || "",
            ready_areas: (r.data.ready_areas || []).join("، "),
            not_ready_areas: (r.data.not_ready_areas || []).join("، "),
            blockers: (r.data.blockers || []).join("، "),
            notes: r.data.notes || "",
            photo_urls: r.data.photo_urls || [],
          });
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/pec/site-readiness", {
        project_id: projectId,
        handover_date: draft.handover_date || null,
        ready_areas: draft.ready_areas.split("،").map(s => s.trim()).filter(Boolean),
        not_ready_areas: draft.not_ready_areas.split("،").map(s => s.trim()).filter(Boolean),
        blockers: draft.blockers.split("،").map(s => s.trim()).filter(Boolean),
        photo_urls: draft.photo_urls,
        notes: draft.notes,
      });
      toast.success("تم حفظ سجل استلام الموقع");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("تعذّر الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await uploadFile(projectId, "site_readiness_photo", f);
      setDraft(d => ({ ...d, photo_urls: [...d.photo_urls, r.url] }));
      toast.success("تم رفع الصورة");
    } catch (err) { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  return (
    <div data-testid="pec-tab-site-readiness" className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-slate-700">تاريخ الاستلام</label>
          <input data-testid="sr-handover-date" type="date" value={draft.handover_date} onChange={e => setDraft(d => ({ ...d, handover_date: e.target.value }))}
                 className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">المناطق الجاهزة (افصل بفاصلة عربية)</label>
          <input data-testid="sr-ready" value={draft.ready_areas} onChange={e => setDraft(d => ({ ...d, ready_areas: e.target.value }))}
                 placeholder="القبو، الدور الأرضي" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">المناطق غير الجاهزة</label>
          <input data-testid="sr-notready" value={draft.not_ready_areas} onChange={e => setDraft(d => ({ ...d, not_ready_areas: e.target.value }))}
                 placeholder="الدور الأول، السطح" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">المعوّقات (Blockers)</label>
          <input data-testid="sr-blockers" value={draft.blockers} onChange={e => setDraft(d => ({ ...d, blockers: e.target.value }))}
                 placeholder="عدم وصول الكهرباء، عدم انتهاء أعمال هدم" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-700">ملاحظات</label>
          <textarea data-testid="sr-notes" rows={3} value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700">صور الاستلام ({draft.photo_urls.length})</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {draft.photo_urls.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" className="group relative inline-flex h-16 w-16 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
              <ImageIcon className="h-5 w-5 text-slate-400" />
              <span className="absolute -bottom-1 -right-1 rounded-full bg-violet-600 px-1.5 text-[10px] text-white">{i + 1}</span>
            </a>
          ))}
          <label data-testid="sr-add-photo" className="inline-flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-violet-400 hover:text-violet-600">
            <Camera className="h-5 w-5" />
            <input type="file" accept="image/*" hidden onChange={onPhoto} />
          </label>
        </div>
      </div>

      <button data-testid="sr-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "جارٍ الحفظ…" : "حفظ"}
      </button>

      {doc && (
        <p className="text-[11px] text-slate-500">آخر تحديث: {(doc.created_at || "").slice(0, 19).replace("T", " ")}</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB: AREAS (recursive tree)
// ─────────────────────────────────────────────────────────────────
function AreasTab({ projectId, onChanged }) {
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ parent_area_id: "", code: "", name: "", area_type: "zone", weight_pct: 0, readiness_pct: 0 });
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const r = await axiosInstance.get(`/pec/areas?project_id=${projectId}`);
    setAreas(r.data.items || []);
  }, [projectId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/areas?project_id=${projectId}`);
        if (alive) setAreas(r.data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("اسم ورمز المنطقة مطلوبان"); return; }
    try {
      await axiosInstance.post("/pec/areas", {
        project_id: projectId,
        parent_area_id: form.parent_area_id || null,
        code: form.code, name: form.name, area_type: form.area_type,
        weight_pct: Number(form.weight_pct), readiness_pct: Number(form.readiness_pct),
      });
      toast.success("تمت إضافة المنطقة");
      setShowForm(false);
      setForm({ parent_area_id: "", code: "", name: "", area_type: "zone", weight_pct: 0, readiness_pct: 0 });
      await load(); onChanged?.();
    } catch (e) { toast.error("فشلت الإضافة"); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذه المنطقة؟")) return;
    await axiosInstance.delete(`/pec/areas/${id}`);
    await load(); onChanged?.();
  };

  // Build tree
  const byParent = {};
  areas.forEach(a => {
    const p = a.parent_area_id || "_root";
    (byParent[p] = byParent[p] || []).push(a);
  });

  const roots = byParent["_root"] || [];

  return (
    <div data-testid="pec-tab-areas" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">شجرة المناطق ({areas.length})</h3>
        <button data-testid="area-add" onClick={() => setShowForm(s => !s)}
                className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
          <Plus className="h-3.5 w-3.5" /> إضافة منطقة
        </button>
      </div>

      {showForm && (
        <div data-testid="area-form" className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 grid gap-2 md:grid-cols-6">
          <input data-testid="area-form-code" placeholder="الرمز (B-GF)" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          <input data-testid="area-form-name" placeholder="الاسم (الدور الأرضي)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm md:col-span-2" />
          <select data-testid="area-form-type" value={form.area_type} onChange={e => setForm(f => ({ ...f, area_type: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="building">Building</option>
            <option value="floor">Floor</option>
            <option value="zone">Zone</option>
          </select>
          <select data-testid="area-form-parent" value={form.parent_area_id} onChange={e => setForm(f => ({ ...f, parent_area_id: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">— لا أب (Root) —</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name} · {a.code}</option>)}
          </select>
          <div className="flex gap-2">
            <input data-testid="area-form-weight" type="number" placeholder="وزن %" value={form.weight_pct} onChange={e => setForm(f => ({ ...f, weight_pct: e.target.value }))} className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            <button data-testid="area-form-save" onClick={save} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">حفظ</button>
          </div>
        </div>
      )}

      <div>{roots.map(r => <AreaNode key={r.id} a={r} depth={0} byParent={byParent} expanded={expanded} setExpanded={setExpanded} del={del} />)}</div>
      {areas.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          لا توجد مناطق بعد — أضف Building / Floor / Zone للبدء بتتبع التنفيذ على مستوى المنطقة.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB: DAILY EXECUTION LOG
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// TAB: BOQ (Bill of Quantities) — Iter-142
// Per-project list of work items: No | Description | Qty | Unit.
// The Daily Execution Log table pulls these as its rows.
// ─────────────────────────────────────────────────────────────────
function BoqTab({ projectId, onChanged }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    line_no: 1, description: "", qty: 0, unit: "", notes: "",
  });

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/pec/boq?project_id=${projectId}`);
      setItems(r.data.items || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setEditingId(null);
    const nextNo = (items[items.length - 1]?.line_no || 0) + 1;
    setForm({ line_no: nextNo, description: "", qty: 0, unit: "", notes: "" });
  };

  const save = async () => {
    if (!form.description.trim() || !form.unit.trim()) {
      toast.error("Description و Unit إلزاميان");
      return;
    }
    try {
      if (editingId) {
        await axiosInstance.patch(`/pec/boq/${editingId}`, {
          line_no:     Number(form.line_no),
          description: form.description.trim(),
          qty:         Number(form.qty),
          unit:        form.unit.trim(),
          notes:       form.notes || null,
        });
        toast.success("تم تعديل البند");
      } else {
        await axiosInstance.post(`/pec/boq`, {
          project_id:  projectId,
          line_no:     Number(form.line_no),
          description: form.description.trim(),
          qty:         Number(form.qty),
          unit:        form.unit.trim(),
          notes:       form.notes || null,
        });
        toast.success("تمت إضافة البند");
      }
      setShowForm(false);
      resetForm();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      line_no:     it.line_no,
      description: it.description,
      qty:         it.qty,
      unit:        it.unit,
      notes:       it.notes || "",
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد حذف هذا البند؟")) return;
    try {
      await axiosInstance.delete(`/pec/boq/${id}`);
      toast.success("تم الحذف");
      await load();
      onChanged?.();
    } catch {
      toast.error("فشل الحذف");
    }
  };

  return (
    <div data-testid="pec-tab-boq" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">
          بنود BOQ ({items.length})
        </h3>
        <button
          data-testid="boq-add"
          onClick={() => { resetForm(); setShowForm(s => !s); }}
          className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
        >
          <Plus className="h-3.5 w-3.5" /> {editingId ? "تعديل" : "إضافة بند"}
        </button>
      </div>

      {showForm && (
        <div data-testid="boq-form" className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-5">
            <div>
              <label className="text-[11px] font-semibold text-slate-700">No</label>
              <input
                data-testid="boq-line-no" type="number" min={1}
                value={form.line_no}
                onChange={e => setForm(f => ({ ...f, line_no: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] font-semibold text-slate-700">Description</label>
              <input
                data-testid="boq-description"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="وصف البند"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">Qty</label>
              <input
                data-testid="boq-qty" type="number" step="0.01" min={0}
                value={form.qty}
                onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">Unit</label>
              <input
                data-testid="boq-unit"
                value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="m³, kg, m², ..."
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">ملاحظات</label>
            <input
              data-testid="boq-notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowForm(false); resetForm(); }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              إلغاء
            </button>
            <button
              data-testid="boq-save"
              onClick={save}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700"
            >
              <Save className="h-4 w-4" /> {editingId ? "حفظ التعديل" : "حفظ"}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="boq-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-right font-semibold w-14">No</th>
              <th className="px-3 py-2 text-right font-semibold">Description</th>
              <th className="px-3 py-2 text-right font-semibold w-24">Qty</th>
              <th className="px-3 py-2 text-right font-semibold w-20">Unit</th>
              <th className="px-3 py-2 text-right font-semibold">ملاحظات</th>
              <th className="px-3 py-2 text-right font-semibold w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  لا توجد بنود في BOQ بعد — أضف البند الأول.
                </td>
              </tr>
            )}
            {items.map(it => (
              <tr key={it.id} data-testid={`boq-row-${it.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-900">{it.line_no}</td>
                <td className="px-3 py-2 text-slate-800">{it.description}</td>
                <td className="px-3 py-2 text-slate-700 font-mono">{fmt(it.qty)}</td>
                <td className="px-3 py-2 text-slate-500">{it.unit}</td>
                <td className="px-3 py-2 text-slate-500 truncate">{it.notes || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button
                      data-testid={`boq-edit-${it.id}`}
                      onClick={() => startEdit(it)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                      title="تعديل"
                    >
                      <FileText className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`boq-delete-${it.id}`}
                      onClick={() => remove(it.id)}
                      className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                      title="حذف"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// TAB: Contract (PEC scope sheet) — Iter-143
// Each project can have several contract scope sheets. Operator picks
// an existing contract number from /api/contracts (project-scoped), adds
// a Subject and a list of contract items, and each item can be linked
// to one or more BOQ rows for traceability.
// ─────────────────────────────────────────────────────────────────
function ContractTab({ projectId, onChanged }) {
  const [contracts, setContracts] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [lookup, setLookup]      = useState([]);
  const [editing, setEditing]    = useState(null);   // null=closed, {}=new, {...}=edit existing
  const [openItem, setOpenItem]  = useState(null);   // contract-line idx with the BOQ picker open

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        axiosInstance.get(`/pec/contracts?project_id=${projectId}`),
        axiosInstance.get(`/pec/boq?project_id=${projectId}`),
        axiosInstance.get(`/pec/contracts/lookup?project_id=${projectId}`),
      ]);
      setContracts(a.data.items || []);
      setBoqItems(b.data.items || []);
      setLookup(c.data.items || []);
    } catch { /* ignore */ }
  }, [projectId]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const startNew = () => {
    setEditing({
      contract_id: null,
      contract_number: "",
      subject: "",
      items: [],
    });
    setOpenItem(null);
  };

  const startEdit = (c) => {
    setEditing({
      id: c.id,
      contract_id: c.contract_id || null,
      contract_number: c.contract_number,
      subject: c.subject || "",
      items: (c.items || []).map(it => ({ ...it, boq_item_ids: it.boq_item_ids || [] })),
    });
    setOpenItem(null);
  };

  const updateItem = (idx, field, value) => {
    setEditing(e => {
      const items = [...e.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...e, items };
    });
  };

  const addItemRow = () => {
    setEditing(e => ({
      ...e,
      items: [...e.items, {
        line_no: (e.items[e.items.length - 1]?.line_no || 0) + 1,
        description: "", qty: 0, unit: "", boq_item_ids: [],
      }],
    }));
  };

  const removeItemRow = (idx) => {
    setEditing(e => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
  };

  const toggleBoqLink = (itemIdx, boqId) => {
    setEditing(e => {
      const items = [...e.items];
      const cur = items[itemIdx].boq_item_ids || [];
      items[itemIdx] = {
        ...items[itemIdx],
        boq_item_ids: cur.includes(boqId)
          ? cur.filter(x => x !== boqId)
          : [...cur, boqId],
      };
      return { ...e, items };
    });
  };

  const onLookupPick = (contractId) => {
    const c = lookup.find(x => x.id === contractId);
    if (!c) return;
    setEditing(e => ({
      ...e,
      contract_id: c.id,
      contract_number: c.contract_number,
      subject: e.subject || c.contract_subject || "",
    }));
  };

  const save = async () => {
    if (!editing.contract_number?.trim()) {
      toast.error("اختر/أدخل رقم العقد"); return;
    }
    try {
      const body = {
        project_id: projectId,
        contract_id: editing.contract_id || null,
        contract_number: editing.contract_number.trim(),
        subject: editing.subject || null,
        items: (editing.items || []).map(it => ({
          line_no: Number(it.line_no),
          description: (it.description || "").trim(),
          qty: Number(it.qty) || 0,
          unit: (it.unit || "").trim(),
          boq_item_ids: it.boq_item_ids || [],
        })).filter(it => it.description && it.unit),
      };
      if (editing.id) {
        await axiosInstance.put(`/pec/contracts/${editing.id}`, body);
        toast.success("تم تعديل العقد");
      } else {
        await axiosInstance.post(`/pec/contracts`, body);
        toast.success("تمت إضافة العقد");
      }
      setEditing(null);
      await loadAll();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد حذف هذا العقد؟")) return;
    try {
      await axiosInstance.delete(`/pec/contracts/${id}`);
      toast.success("تم الحذف");
      await loadAll();
    } catch { toast.error("فشل الحذف"); }
  };

  const boqLabel = (id) => {
    const b = boqItems.find(x => x.id === id);
    return b ? `${b.line_no}. ${b.description} (${fmt(b.qty)} ${b.unit})` : id;
  };

  return (
    <div data-testid="pec-tab-contract" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">
          عقود المشروع ({contracts.length})
        </h3>
        <button
          data-testid="contract-add"
          onClick={startNew}
          className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
        >
          <Plus className="h-3.5 w-3.5" /> إضافة عقد
        </button>
      </div>

      {editing && (
        <div data-testid="contract-form" className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-[11px] font-semibold text-slate-700">رقم العقد</label>
              {lookup.length > 0 ? (
                <select
                  data-testid="contract-number-select"
                  value={editing.contract_id || ""}
                  onChange={(e) => onLookupPick(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">— اختر من قائمة العقود —</option>
                  {lookup.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.contract_number}{c.contract_subject ? ` · ${c.contract_subject}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid="contract-number-input"
                  value={editing.contract_number}
                  onChange={(e) => setEditing(s => ({ ...s, contract_number: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="ادخل رقم العقد (لا توجد عقود مرتبطة بالمشروع)"
                />
              )}
              {editing.contract_number && (
                <div className="mt-1 text-[10px] text-emerald-700">
                  المختار: <b>{editing.contract_number}</b>
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">Subject</label>
              <input
                data-testid="contract-subject"
                value={editing.subject || ""}
                onChange={(e) => setEditing(s => ({ ...s, subject: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="موضوع/نطاق العقد"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-900">
              بنود العقد ({editing.items.length})
            </h4>
            <button
              data-testid="contract-item-add"
              onClick={addItemRow}
              className="inline-flex items-center gap-1 rounded-md bg-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-300"
            >
              <Plus className="h-3 w-3" /> إضافة بند
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-2 py-1.5 text-right font-semibold w-12">No</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Description</th>
                  <th className="px-2 py-1.5 text-right font-semibold w-20">Qty</th>
                  <th className="px-2 py-1.5 text-right font-semibold w-20">Unit</th>
                  <th className="px-2 py-1.5 text-right font-semibold">BOQ المرتبطة</th>
                  <th className="px-2 py-1.5 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {editing.items.length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">
                    لا توجد بنود — أضف بنداً للبدء
                  </td></tr>
                )}
                {editing.items.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <tr data-testid={`contract-item-row-${idx}`}>
                      <td className="px-2 py-1.5">
                        <input type="number" min={1} value={it.line_no}
                          onChange={e => updateItem(idx, "line_no", e.target.value)}
                          className="w-full rounded border border-slate-300 px-1.5 py-0.5 text-right" />
                      </td>
                      <td className="px-2 py-1.5">
                        <textarea value={it.description}
                          onChange={e => updateItem(idx, "description", e.target.value)}
                          data-testid={`contract-item-desc-${idx}`}
                          rows={2}
                          className="w-full rounded border border-slate-300 px-1.5 py-0.5 resize-y leading-snug" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" step="0.01" min={0} value={it.qty}
                          onChange={e => updateItem(idx, "qty", e.target.value)}
                          className="w-full rounded border border-slate-300 px-1.5 py-0.5 text-right" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={it.unit}
                          onChange={e => updateItem(idx, "unit", e.target.value)}
                          className="w-full rounded border border-slate-300 px-1.5 py-0.5" />
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          data-testid={`contract-item-add-boq-${idx}`}
                          onClick={() => setOpenItem(openItem === idx ? null : idx)}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 text-[11px] hover:bg-emerald-100"
                        >
                          <Plus className="h-3 w-3" />
                          {(it.boq_item_ids?.length || 0) === 0
                            ? "اضف ايتم BOQ"
                            : `${it.boq_item_ids.length} ايتم BOQ`}
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => removeItemRow(idx)}
                          className="rounded p-1 text-rose-500 hover:bg-rose-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                    {openItem === idx && (
                      <tr data-testid={`contract-boq-picker-${idx}`}>
                        <td colSpan={6} className="px-3 py-2 bg-emerald-50/30 border-t border-emerald-100">
                          {boqItems.length === 0 ? (
                            <p className="text-[11px] text-slate-500">لا توجد بنود BOQ — أضفها أولاً من تبويب BOQ.</p>
                          ) : (
                            <>
                              <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
                                اختر بنود BOQ المرتبطة بهذا البند:
                              </p>
                              <div className="grid gap-1 md:grid-cols-2">
                                {boqItems.map(b => (
                                  <label key={b.id} className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-white rounded px-1.5 py-0.5">
                                    <input
                                      data-testid={`boq-checkbox-${idx}-${b.id}`}
                                      type="checkbox"
                                      checked={(it.boq_item_ids || []).includes(b.id)}
                                      onChange={() => toggleBoqLink(idx, b.id)}
                                    />
                                    <span><b>{b.line_no}.</b> {b.description} <span className="text-slate-400">({fmt(b.qty)} {b.unit})</span></span>
                                  </label>
                                ))}
                              </div>
                              {(it.boq_item_ids || []).length > 0 && (
                                <div className="mt-2 text-[10px] text-emerald-700">
                                  ✓ تم ربط {it.boq_item_ids.length} بند(ا)
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(null)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
              إلغاء
            </button>
            <button data-testid="contract-save" onClick={save}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
              <Save className="h-4 w-4" /> حفظ العقد
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {contracts.length === 0 && !editing && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-8 text-center text-xs text-slate-500">
            لا توجد عقود مضافة بعد لهذا المشروع
          </div>
        )}
        {contracts.map(c => (
          <div key={c.id} data-testid={`contract-row-${c.id}`}
               className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="text-sm font-bold text-slate-900">
                  {c.contract_number}
                </h4>
                {c.subject && (<p className="text-xs text-slate-600 mt-0.5">{c.subject}</p>)}
              </div>
              <div className="flex gap-1">
                <button data-testid={`contract-edit-${c.id}`} onClick={() => startEdit(c)}
                  className="inline-flex items-center gap-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 text-[11px] font-semibold hover:bg-indigo-100"
                  title="تعديل">
                  <FileText className="h-3 w-3" /> تعديل
                </button>
                <button data-testid={`contract-delete-${c.id}`} onClick={() => remove(c.id)}
                  className="inline-flex items-center gap-1 rounded-md bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 text-[11px] font-semibold hover:bg-rose-100"
                  title="حذف">
                  <Trash2 className="h-3 w-3" /> حذف
                </button>
              </div>
            </div>
            {(c.items?.length || 0) > 0 && (
              <table className="w-full text-xs mt-2">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-1 text-right font-semibold w-12">No</th>
                    <th className="px-2 py-1 text-right font-semibold">Description</th>
                    <th className="px-2 py-1 text-right font-semibold w-20">Qty</th>
                    <th className="px-2 py-1 text-right font-semibold w-20">Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {c.items.map(it => {
                    const linkedBoq = (it.boq_item_ids || [])
                      .map(id => boqItems.find(b => b.id === id))
                      .filter(Boolean);
                    return (
                      <React.Fragment key={it.line_no}>
                        <tr className="bg-indigo-50/30 font-semibold">
                          <td className="px-2 py-1 text-indigo-900 align-top">{it.line_no}</td>
                          <td className="px-2 py-1 text-slate-900 whitespace-pre-line">{it.description}</td>
                          <td className="px-2 py-1 font-mono align-top">{fmt(it.qty)}</td>
                          <td className="px-2 py-1 text-slate-700 align-top">{it.unit}</td>
                        </tr>
                        {linkedBoq.length === 0 && (
                          <tr><td colSpan={4} className="px-6 py-1 text-[10px] text-slate-400 italic">
                            — لم يتم ربط بنود BOQ —
                          </td></tr>
                        )}
                        {linkedBoq.map((b, bIdx) => (
                          <tr key={b.id} data-testid={`contract-boq-sub-${it.line_no}-${b.id}`}
                              className="bg-emerald-50/20">
                            <td className="px-2 py-1 pr-8 text-emerald-700 font-mono text-[11px]">
                              {String.fromCharCode(97 + bIdx)}.
                            </td>
                            <td className="px-2 py-1 text-slate-700">{b.description}</td>
                            <td className="px-2 py-1 font-mono text-emerald-700">{fmt(b.qty)}</td>
                            <td className="px-2 py-1 text-slate-600">{b.unit}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function DailyLogTab({ projectId, areas, onChanged }) {
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);   // Iter-143 edit support
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    log_date: today, area_id: "", labor_planned: 0, labor_actual: 0,
    equipment_active: 0, notes: "", engineer_name: "", photo_urls: [],
    boq_progress: [],
  });
  const [boqLoading, setBoqLoading] = useState(false);
  const [boqSource, setBoqSource] = useState(null);   // 'previous_day' | 'boq_original' | 'boq_empty'
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    const r = await axiosInstance.get(`/pec/daily-logs?project_id=${projectId}`);
    setLogs(r.data.items || []);
  }, [projectId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/daily-logs?project_id=${projectId}`);
        if (alive) setLogs(r.data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Iter-142 — when the user opens the new-day form, fetch the BOQ
  // rows with their carry-forward starting Qty (= yesterday's remaining
  // or BOQ original Qty when no previous day exists).
  const openNewDayForm = useCallback(async () => {
    if (showForm) { setShowForm(false); setEditingLogId(null); return; }
    setEditingLogId(null);
    setBoqLoading(true);
    try {
      const r = await axiosInstance.get(
        `/pec/daily-logs/contract-starting?project_id=${projectId}`
      );
      setForm(f => ({ ...f, log_date: today, boq_progress: r.data.items || [] }));
      setBoqSource(r.data.from);
    } catch {
      setForm(f => ({ ...f, boq_progress: [] }));
      setBoqSource(null);
    } finally {
      setBoqLoading(false);
      setShowForm(true);
    }
  }, [projectId, showForm, today]);

  // Iter-143 — open the form to EDIT an existing log. Pre-fills all
  // fields including boq_progress so the engineer can adjust today's
  // installed/used qtys without re-typing the whole sheet.
  const startEditLog = useCallback((log) => {
    setEditingLogId(log.id);
    setForm({
      log_date: log.log_date || today,
      area_id: log.area_id || "",
      labor_planned: log.labor_planned || 0,
      labor_actual: log.labor_actual || 0,
      equipment_active: log.equipment_active || 0,
      notes: log.notes || "",
      engineer_name: log.engineer_name || "",
      photo_urls: log.photo_urls || [],
      boq_progress: (log.boq_progress || []).map(r => ({ ...r })),
    });
    setBoqSource(null);
    setShowForm(true);
  }, [today]);

  // Mutate a single BOQ row in the form. ``qty_remaining`` is always
  // derived = qty_start - qty_used_today (clamped at 0).
  const updateBoqRow = (idx, field, value) => {
    setForm(f => {
      const next = [...(f.boq_progress || [])];
      const row = { ...next[idx], [field]: value };
      const start = Number(row.qty_start) || 0;
      const used  = Number(row.qty_used_today) || 0;
      row.qty_remaining = Math.max(0, start - used);
      next[idx] = row;
      return { ...f, boq_progress: next };
    });
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await uploadFile(projectId, "daily_photo", f);
      setForm(d => ({ ...d, photo_urls: [...d.photo_urls, r.url] }));
      toast.success("تم رفع الصورة");
    } catch { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  const save = async () => {
    try {
      const rows = form.boq_progress || [];
      const qty_executed_total      = rows.reduce(
        (s, r) => s + (Number(r.qty_installed_today) || 0), 0,
      );
      const material_consumed_qty   = rows.reduce(
        (s, r) => s + (Number(r.qty_used_today) || 0), 0,
      );
      const material_remaining_qty  = rows.reduce(
        (s, r) => s + (Number(r.qty_remaining) || 0), 0,
      );
      const payload = {
        project_id: projectId,
        area_id: form.area_id || null,
        log_date: form.log_date,
        labor_planned: Number(form.labor_planned),
        labor_actual: Number(form.labor_actual),
        equipment_active: Number(form.equipment_active),
        qty_executed_total,
        material_consumed_qty,
        material_remaining_qty,
        boq_progress: rows.map(r => ({
          boq_item_id:         r.boq_item_id,
          line_no:             r.line_no,
          description:         r.description,
          unit:                r.unit,
          qty_start:           Number(r.qty_start) || 0,
          qty_installed_today: Number(r.qty_installed_today) || 0,
          qty_used_today:      Number(r.qty_used_today) || 0,
          qty_remaining:       Number(r.qty_remaining) || 0,
        })),
        notes: form.notes,
        engineer_name: form.engineer_name,
        photo_urls: form.photo_urls,
      };
      if (editingLogId) {
        await axiosInstance.patch(`/pec/daily-logs/${editingLogId}`, payload);
        toast.success("تم تعديل السجل اليومي");
      } else {
        await axiosInstance.post("/pec/daily-logs", payload);
        toast.success("تم حفظ السجل اليومي");
      }
      setShowForm(false);
      setEditingLogId(null);
      setForm({
        log_date: today, area_id: "", labor_planned: 0, labor_actual: 0,
        equipment_active: 0, notes: "", engineer_name: "",
        photo_urls: [], boq_progress: [],
      });
      await load(); onChanged?.();
    } catch (e) { toast.error("فشل الحفظ"); }
  };

  return (
    <div data-testid="pec-tab-daily" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">سجل التنفيذ اليومي ({logs.length})</h3>
        <button data-testid="daily-add" onClick={openNewDayForm}
                className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
          <Plus className="h-3.5 w-3.5" /> سجل اليوم
        </button>
      </div>

      {showForm && (
        <div data-testid="daily-form" className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-700">التاريخ</label>
              <input data-testid="daily-date" type="date" value={form.log_date} onChange={e => setForm(f => ({ ...f, log_date: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">المنطقة</label>
              <select data-testid="daily-area" value={form.area_id} onChange={e => setForm(f => ({ ...f, area_id: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                <option value="">— كل المشروع —</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">العمال المخططون</label>
              <input data-testid="daily-labor-planned" type="number" value={form.labor_planned} onChange={e => setForm(f => ({ ...f, labor_planned: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">العمال الفعليون</label>
              <input data-testid="daily-labor-actual" type="number" value={form.labor_actual} onChange={e => setForm(f => ({ ...f, labor_actual: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">المعدات العاملة</label>
              <input data-testid="daily-equipment" type="number" value={form.equipment_active} onChange={e => setForm(f => ({ ...f, equipment_active: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">المهندس</label>
              <input data-testid="daily-engineer" value={form.engineer_name} onChange={e => setForm(f => ({ ...f, engineer_name: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
              <h4 className="text-xs font-bold text-slate-900">
                جدول البنود — اليوم
              </h4>
              {boqSource === "previous_day" && (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="boq-source-prev">
                  Qty محسوبة من متبقي اليوم السابق
                </span>
              )}
              {boqSource === "contract_original" && (
                <span className="text-[10px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full" data-testid="boq-source-contract">
                  Qty من بنود العقد (أول يوم)
                </span>
              )}
              {boqSource === "boq_original" && (
                <span className="text-[10px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full" data-testid="boq-source-original">
                  Qty من BOQ الأصلي (لا توجد عقود)
                </span>
              )}
              {boqSource === "boq_empty" && (
                <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  أضف بنود العقد أو BOQ أولاً لتظهر هنا
                </span>
              )}
            </div>
            {boqLoading ? (
              <div className="p-4 text-center text-xs text-slate-500">
                <RefreshCw className="inline h-4 w-4 animate-spin mr-1" />
                جاري تحميل بنود BOQ...
              </div>
            ) : (form.boq_progress?.length ?? 0) === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">
                لا توجد بنود — اذهب إلى تبويب Contract وأضف عقداً ببنوده، أو إلى تبويب BOQ.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="daily-boq-table">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-right font-semibold w-10">No</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Description</th>
                      <th className="px-2 py-1.5 text-right font-semibold w-20">Qty</th>
                      <th className="px-2 py-1.5 text-right font-semibold w-16">Unit</th>
                      <th className="px-2 py-1.5 text-right font-semibold w-28 text-emerald-700">Qty Installed Today</th>
                      <th className="px-2 py-1.5 text-right font-semibold w-28 text-amber-700">Qty Used Today</th>
                      <th className="px-2 py-1.5 text-right font-semibold w-28 text-sky-700">Qty Remaining</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {form.boq_progress.map((row, idx) => (
                      <tr key={row.boq_item_id} data-testid={`daily-boq-row-${row.boq_item_id}`}>
                        <td className="px-2 py-1.5 font-semibold">{row.line_no}</td>
                        <td className="px-2 py-1.5 text-slate-800">{row.description}</td>
                        <td className="px-2 py-1.5 font-mono">{fmt(row.qty_start)}</td>
                        <td className="px-2 py-1.5 text-slate-500">{row.unit}</td>
                        <td className="px-2 py-1.5">
                          <input
                            data-testid={`boq-installed-${row.boq_item_id}`}
                            type="number" step="0.01" min={0}
                            value={row.qty_installed_today}
                            onChange={e => updateBoqRow(idx, "qty_installed_today", e.target.value)}
                            className="w-full rounded border border-emerald-300 bg-emerald-50/30 px-2 py-1 text-right font-bold text-emerald-900"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            data-testid={`boq-used-${row.boq_item_id}`}
                            type="number" step="0.01" min={0}
                            value={row.qty_used_today}
                            onChange={e => updateBoqRow(idx, "qty_used_today", e.target.value)}
                            className="w-full rounded border border-amber-300 bg-amber-50/30 px-2 py-1 text-right font-bold text-amber-900"
                          />
                        </td>
                        <td
                          data-testid={`boq-remaining-${row.boq_item_id}`}
                          className="px-2 py-1.5 text-right font-mono font-bold text-sky-700"
                        >
                          {fmt(row.qty_remaining)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">ملاحظات</label>
            <textarea data-testid="daily-notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {form.photo_urls.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50"><ImageIcon className="h-4 w-4 text-slate-400" /></a>
              ))}
              <label data-testid="daily-add-photo" className="inline-flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-violet-400">
                <Camera className="h-4 w-4" />
                <input type="file" accept="image/*" hidden onChange={onPhoto} />
              </label>
            </div>
            <button data-testid="daily-save" onClick={save} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
              <Save className="h-4 w-4" /> {editingLogId ? "حفظ التعديل" : "حفظ السجل"}
            </button>
          </div>
        </div>
      )}

      <div data-testid="daily-list" className="space-y-2">
        {logs.map(l => {
          const isOpen = openRow === l.id;
          return (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="grid w-full gap-2 p-3 text-right md:grid-cols-8 items-center hover:bg-slate-50">
                <button
                  data-testid={`daily-row-${l.id}`}
                  onClick={() => setOpenRow(isOpen ? null : l.id)}
                  className="md:col-span-7 grid gap-2 md:grid-cols-7 items-center text-right active:bg-slate-100 transition-all"
                >
                  <div className="text-xs font-semibold text-slate-900">{l.log_date}</div>
                  <div className="text-xs text-slate-600"><Users className="inline h-3.5 w-3.5 mr-1" /> {l.labor_actual}/{l.labor_planned}</div>
                  <div className="text-xs text-slate-600">معدات: {l.equipment_active}</div>
                  <div className="text-xs text-emerald-700 font-bold">منفذ: {fmt(l.qty_executed_total || 0)}</div>
                  <div className="text-xs text-amber-700 font-bold">مستهلك: {fmt(l.material_consumed_qty || 0)}</div>
                  <div className="text-xs text-slate-500 truncate">{l.notes || "—"}</div>
                  <div className="text-xs text-slate-500 text-left">{(l.photo_urls || []).length} 📸 {isOpen ? "▲" : "▼"}</div>
                </button>
                <div className="flex justify-end gap-1">
                  <button
                    data-testid={`daily-edit-${l.id}`}
                    onClick={(e) => { e.stopPropagation(); startEditLog(l); }}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 text-[11px] font-semibold hover:bg-indigo-100"
                    title="تعديل السجل"
                  >
                    <FileText className="h-3 w-3" /> تعديل
                  </button>
                </div>
              </div>
              {isOpen && (
                <div data-testid={`daily-detail-${l.id}`} className="border-t border-slate-200 bg-slate-50 p-4 grid gap-2 md:grid-cols-2 text-xs">
                  <div><b className="text-slate-700">المنطقة:</b> {l.area_id || "—"}</div>
                  <div><b className="text-slate-700">المهندس:</b> {l.engineer_name || "—"}</div>
                  <div><b className="text-slate-700">العمال (مخطط/فعلي):</b> {l.labor_planned} / {l.labor_actual}</div>
                  <div><b className="text-slate-700">المعدات العاملة:</b> {l.equipment_active}</div>
                  <div><b className="text-emerald-700">الكمية المنفذة:</b> {fmt(l.qty_executed_total || 0)}</div>
                  <div><b className="text-amber-700">المواد المستهلكة:</b> {fmt(l.material_consumed_qty || 0)}</div>
                  <div><b className="text-sky-700">المواد المتبقية:</b> {fmt(l.material_remaining_qty || 0)}</div>
                  <div><b className="text-slate-700">GPS:</b> {l.gps ? `${l.gps.lat?.toFixed(4)}, ${l.gps.lng?.toFixed(4)}` : "—"}</div>
                  <div className="md:col-span-2"><b className="text-slate-700">الملاحظات:</b> {l.notes || "—"}</div>
                  {(l.photo_urls || []).length > 0 && (
                    <div className="md:col-span-2">
                      <b className="text-slate-700">الصور ({l.photo_urls.length}):</b>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {l.photo_urls.map((u, i) => (
                          <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded border border-slate-300 bg-white"><ImageIcon className="h-4 w-4 text-slate-400" /></a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2 text-[10px] text-slate-400">created: {l.created_at}</div>
                  {(l.boq_progress || []).length > 0 && (
                    <div className="md:col-span-2 mt-2">
                      <b className="text-slate-700">جدول البنود ({l.boq_progress.length}):</b>
                      <div className="mt-1 overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table className="w-full text-[11px]">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-2 py-1 text-right font-semibold">No</th>
                              <th className="px-2 py-1 text-right font-semibold">Description</th>
                              <th className="px-2 py-1 text-right font-semibold">Qty</th>
                              <th className="px-2 py-1 text-right font-semibold">Unit</th>
                              <th className="px-2 py-1 text-right font-semibold text-emerald-700">Installed</th>
                              <th className="px-2 py-1 text-right font-semibold text-amber-700">Used</th>
                              <th className="px-2 py-1 text-right font-semibold text-sky-700">Remaining</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {l.boq_progress.map(r => (
                              <tr key={r.boq_item_id}>
                                <td className="px-2 py-1 font-semibold">{r.line_no}</td>
                                <td className="px-2 py-1 text-slate-800">{r.description}</td>
                                <td className="px-2 py-1 font-mono">{fmt(r.qty_start)}</td>
                                <td className="px-2 py-1 text-slate-500">{r.unit}</td>
                                <td className="px-2 py-1 text-right font-mono text-emerald-700">{fmt(r.qty_installed_today)}</td>
                                <td className="px-2 py-1 text-right font-mono text-amber-700">{fmt(r.qty_used_today)}</td>
                                <td className="px-2 py-1 text-right font-mono font-bold text-sky-700">{fmt(r.qty_remaining)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {logs.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            لم يُسجَّل أي يوم بعد — أضف أول سجل تنفيذ يومي.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB: MAR (Material Approval Request) with upload
// ─────────────────────────────────────────────────────────────────
function MARTab({ projectId, areas, onChanged }) {
  const [data, setData] = useState({ items: [], by_status: {} });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ received_date: new Date().toISOString().slice(0, 10), area_id: "", materials: "", status: "Draft", notes: "", file_url: "", photo_urls: [] });

  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    const r = await axiosInstance.get(`/pec/mar?project_id=${projectId}`);
    setData(r.data);
  }, [projectId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/mar?project_id=${projectId}`);
        if (alive) setData(r.data);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const onFile = async (kind, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await uploadFile(projectId, kind === "doc" ? "mar_doc" : "mar_photo", f);
      if (kind === "doc") setForm(d => ({ ...d, file_url: r.url }));
      else setForm(d => ({ ...d, photo_urls: [...d.photo_urls, r.url] }));
      toast.success("تم الرفع");
    } catch { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  const save = async () => {
    try {
      await axiosInstance.post("/pec/mar", {
        project_id: projectId,
        area_id: form.area_id || null,
        received_date: form.received_date,
        materials: form.materials ? form.materials.split("،").map(m => ({ name: m.trim() })).filter(m => m.name) : [],
        status: form.status,
        notes: form.notes,
        file_url: form.file_url || null,
        photo_urls: form.photo_urls,
      });
      toast.success("تم حفظ MAR");
      setShowForm(false);
      setForm({ received_date: new Date().toISOString().slice(0, 10), area_id: "", materials: "", status: "Draft", notes: "", file_url: "", photo_urls: [] });
      await load(); onChanged?.();
    } catch (e) { toast.error("فشل الحفظ"); }
  };

  const changeStatus = async (m, ns) => {
    try {
      await axiosInstance.patch(`/pec/mar/${m.id}`, { status: ns, consultant_approved: ns === "Approved" });
      toast.success(`MAR → ${ns}`);
      await load(); onChanged?.();
    } catch { toast.error("فشل تغيير الحالة"); }
  };

  return (
    <div data-testid="pec-tab-mar" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-xs">
          {MAR_STATUSES.map(s => (
            <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{s}: <b className="text-violet-700">{data.by_status?.[s] || 0}</b></span>
          ))}
        </div>
        <button data-testid="mar-add" onClick={() => setShowForm(s => !s)} className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
          <Plus className="h-3.5 w-3.5" /> MAR جديد
        </button>
      </div>

      {showForm && (
        <div data-testid="mar-form" className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-700">تاريخ الاستلام</label>
              <input data-testid="mar-date" type="date" value={form.received_date} onChange={e => setForm(f => ({ ...f, received_date: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">المنطقة</label>
              <select data-testid="mar-area" value={form.area_id} onChange={e => setForm(f => ({ ...f, area_id: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                <option value="">— —</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">الحالة</label>
              <select data-testid="mar-status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                {MAR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <label data-testid="mar-doc-upload" className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:border-violet-400">
                <Upload className="h-3.5 w-3.5" /> {form.file_url ? "تم الرفع" : "ملف MAR (PDF)"}
                <input type="file" hidden accept=".pdf,image/*" onChange={(e) => onFile("doc", e)} />
              </label>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">المواد (افصل بفاصلة عربية)</label>
            <input data-testid="mar-materials" value={form.materials} onChange={e => setForm(f => ({ ...f, materials: e.target.value }))} placeholder="حديد، أسمنت، رمل" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">ملاحظات</label>
            <textarea data-testid="mar-notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {form.photo_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200"><ImageIcon className="h-4 w-4 text-slate-400" /></a>)}
              <label data-testid="mar-photo" className="inline-flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-400">
                <Camera className="h-4 w-4 text-slate-400" />
                <input type="file" accept="image/*" hidden onChange={(e) => onFile("photo", e)} />
              </label>
            </div>
            <button data-testid="mar-save" onClick={save} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
              <Save className="h-4 w-4" /> حفظ
            </button>
          </div>
        </div>
      )}

      <div data-testid="mar-list" className="space-y-2">
        {(data.items || []).map(m => {
          const isOpen = openRow === m.id;
          return (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="grid gap-2 md:grid-cols-6 items-center p-3">
                <button data-testid={`mar-row-${m.id}`} onClick={() => setOpenRow(isOpen ? null : m.id)} className="text-xs font-bold text-violet-700 text-right hover:underline">{m.mar_number} {isOpen ? "▲" : "▼"}</button>
                <div className="text-xs text-slate-700">{m.received_date}</div>
                <div className="text-xs text-slate-500 truncate md:col-span-2">{(m.materials || []).map(x => x.name).join("، ") || "—"}</div>
                <select value={m.status} onChange={(e) => changeStatus(m, e.target.value)} data-testid={`mar-status-${m.id}`} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">
                  {MAR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="text-xs text-slate-500 text-left">
                  {m.file_url && <a href={m.file_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline mr-2"><FileText className="inline h-3.5 w-3.5" /></a>}
                  {(m.photo_urls || []).length > 0 && <span className="text-slate-400">{m.photo_urls.length} 📸</span>}
                </div>
              </div>
              {isOpen && (
                <div data-testid={`mar-detail-${m.id}`} className="border-t border-slate-200 bg-slate-50 p-4 grid gap-2 md:grid-cols-2 text-xs">
                  <div><b>الرقم:</b> {m.mar_number}</div>
                  <div><b>التاريخ:</b> {m.received_date}</div>
                  <div><b>المنطقة:</b> {m.area_id || "—"}</div>
                  <div><b>الحالة:</b> {m.status}</div>
                  <div><b>اعتماد الاستشاري:</b> {m.consultant_approved ? "نعم ✓" : "—"}</div>
                  <div><b>GPS:</b> {m.gps ? `${m.gps.lat?.toFixed(4)}, ${m.gps.lng?.toFixed(4)}` : "—"}</div>
                  <div className="md:col-span-2"><b>المواد:</b> {(m.materials || []).map(x => x.name).join("، ") || "—"}</div>
                  <div className="md:col-span-2"><b>ملاحظات:</b> {m.notes || "—"}</div>
                  {m.file_url && <div className="md:col-span-2"><a href={m.file_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">📄 فتح ملف MAR</a></div>}
                  {(m.photo_urls || []).length > 0 && (
                    <div className="md:col-span-2">
                      <b>الصور ({m.photo_urls.length}):</b>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {m.photo_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded border border-slate-300 bg-white"><ImageIcon className="h-4 w-4 text-slate-400" /></a>)}
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2 text-[10px] text-slate-400">created: {m.created_at}</div>
                </div>
              )}
            </div>
          );
        })}
        {(data.items || []).length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">لا توجد MARs بعد.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB: MIR with upload & evidence (before/after)
// ─────────────────────────────────────────────────────────────────
function MIRTab({ projectId, areas, onChanged }) {
  const [data, setData] = useState({ items: [], by_status: {} });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ inspection_date: new Date().toISOString().slice(0, 10), area_id: "", quantity_executed: 0, consultant_name: "", status: "Pending", notes: "", file_url: "", photo_urls: [], before_photo_urls: [], after_photo_urls: [] });

  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    const r = await axiosInstance.get(`/pec/mir?project_id=${projectId}`);
    setData(r.data);
  }, [projectId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/pec/mir?project_id=${projectId}`);
        if (alive) setData(r.data);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const onFile = async (kind, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const r = await uploadFile(projectId, `mir_${kind}`, f);
      if (kind === "doc") setForm(d => ({ ...d, file_url: r.url }));
      else if (kind === "before") setForm(d => ({ ...d, before_photo_urls: [...d.before_photo_urls, r.url] }));
      else if (kind === "after") setForm(d => ({ ...d, after_photo_urls: [...d.after_photo_urls, r.url] }));
      else setForm(d => ({ ...d, photo_urls: [...d.photo_urls, r.url] }));
      toast.success("تم الرفع");
    } catch { toast.error("فشل الرفع"); }
    e.target.value = "";
  };

  const save = async () => {
    try {
      await axiosInstance.post("/pec/mir", {
        project_id: projectId,
        area_id: form.area_id || null,
        inspection_date: form.inspection_date,
        quantity_executed: Number(form.quantity_executed),
        consultant_name: form.consultant_name,
        status: form.status,
        notes: form.notes,
        file_url: form.file_url || null,
        photo_urls: form.photo_urls,
        before_photo_urls: form.before_photo_urls,
        after_photo_urls: form.after_photo_urls,
      });
      toast.success("تم حفظ MIR");
      setShowForm(false);
      setForm({ inspection_date: new Date().toISOString().slice(0, 10), area_id: "", quantity_executed: 0, consultant_name: "", status: "Pending", notes: "", file_url: "", photo_urls: [], before_photo_urls: [], after_photo_urls: [] });
      await load(); onChanged?.();
    } catch (e) { toast.error("فشل الحفظ"); }
  };

  const changeStatus = async (m, ns) => {
    try {
      await axiosInstance.patch(`/pec/mir/${m.id}`, { status: ns });
      toast.success(`MIR → ${ns}${ns === "Approved" ? " · Ready For Billing 🟢" : ""}`);
      await load(); onChanged?.();
    } catch { toast.error("فشل تغيير الحالة"); }
  };

  return (
    <div data-testid="pec-tab-mir" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-xs">
          {MIR_STATUSES.map(s => (
            <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">{s}: <b className="text-violet-700">{data.by_status?.[s] || 0}</b></span>
          ))}
          {data.ready_for_billing_qty > 0 && (
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-bold text-emerald-700">جاهز للفوترة: {fmt(data.ready_for_billing_qty)}</span>
          )}
        </div>
        <button data-testid="mir-add" onClick={() => setShowForm(s => !s)} className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
          <Plus className="h-3.5 w-3.5" /> MIR جديد
        </button>
      </div>

      {showForm && (
        <div data-testid="mir-form" className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-700">تاريخ الفحص</label>
              <input data-testid="mir-date" type="date" value={form.inspection_date} onChange={e => setForm(f => ({ ...f, inspection_date: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">المنطقة</label>
              <select data-testid="mir-area" value={form.area_id} onChange={e => setForm(f => ({ ...f, area_id: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                <option value="">— —</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">الكمية المنفذة</label>
              <input data-testid="mir-qty" type="number" value={form.quantity_executed} onChange={e => setForm(f => ({ ...f, quantity_executed: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">الاستشاري</label>
              <input data-testid="mir-consultant" value={form.consultant_name} onChange={e => setForm(f => ({ ...f, consultant_name: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700">الحالة</label>
              <select data-testid="mir-status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                {MIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label data-testid="mir-doc-upload" className="inline-flex w-full cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:border-violet-400">
                <Upload className="h-3.5 w-3.5" /> {form.file_url ? "تم الرفع" : "ملف MIR"}
                <input type="file" hidden accept=".pdf,image/*" onChange={(e) => onFile("doc", e)} />
              </label>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <PhotoStripStatic label="قبل التنفيذ"  urls={form.before_photo_urls} onAdd={(e) => onFile("before", e)} testid="mir-photo-before" />
            <PhotoStripStatic label="أثناء التنفيذ" urls={form.photo_urls}        onAdd={(e) => onFile("during", e)} testid="mir-photo-during" />
            <PhotoStripStatic label="بعد التنفيذ"  urls={form.after_photo_urls}  onAdd={(e) => onFile("after", e)}  testid="mir-photo-after" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700">ملاحظات</label>
            <textarea data-testid="mir-notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <button data-testid="mir-save" onClick={save} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
            <Save className="h-4 w-4" /> حفظ MIR
          </button>
        </div>
      )}

      <div data-testid="mir-list" className="space-y-2">
        {(data.items || []).map(m => {
          const isOpen = openRow === m.id;
          return (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="grid gap-2 md:grid-cols-6 items-center p-3">
                <button data-testid={`mir-row-${m.id}`} onClick={() => setOpenRow(isOpen ? null : m.id)} className="text-xs font-bold text-violet-700 text-right hover:underline">{m.mir_number} {isOpen ? "▲" : "▼"}</button>
                <div className="text-xs text-slate-700">{m.inspection_date}</div>
                <div className="text-xs text-slate-700">qty: <b>{m.quantity_executed}</b></div>
                <select value={m.status} onChange={(e) => changeStatus(m, e.target.value)} data-testid={`mir-status-${m.id}`} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">
                  {MIR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="text-xs text-slate-500 truncate">{m.consultant_name || "—"}</div>
                <div className="text-xs text-slate-500 text-left">
                  {m.file_url && <a href={m.file_url} target="_blank" rel="noreferrer" className="text-violet-600 mr-2"><FileText className="inline h-3.5 w-3.5" /></a>}
                  <span className="text-slate-400">{(m.before_photo_urls || []).length}+{(m.photo_urls || []).length}+{(m.after_photo_urls || []).length} 📸</span>
                </div>
              </div>
              {isOpen && (
                <div data-testid={`mir-detail-${m.id}`} className="border-t border-slate-200 bg-slate-50 p-4 grid gap-2 md:grid-cols-2 text-xs">
                  <div><b>الرقم:</b> {m.mir_number}</div>
                  <div><b>تاريخ الفحص:</b> {m.inspection_date}</div>
                  <div><b>المنطقة:</b> {m.area_id || "—"}</div>
                  <div><b>الحالة:</b> {m.status}</div>
                  <div><b>الكمية المنفذة:</b> {m.quantity_executed}</div>
                  <div><b>الاستشاري:</b> {m.consultant_name || "—"}</div>
                  <div><b>GPS:</b> {m.gps ? `${m.gps.lat?.toFixed(4)}, ${m.gps.lng?.toFixed(4)}` : "—"}</div>
                  <div className="md:col-span-2"><b>ملاحظات:</b> {m.notes || "—"}</div>
                  {m.file_url && <div className="md:col-span-2"><a href={m.file_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">📄 فتح ملف MIR</a></div>}
                  {[["قبل", m.before_photo_urls], ["أثناء", m.photo_urls], ["بعد", m.after_photo_urls]].map(([lbl, urls]) => (urls || []).length > 0 && (
                    <div key={lbl} className="md:col-span-2">
                      <b>{lbl} ({urls.length}):</b>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded border border-slate-300 bg-white"><ImageIcon className="h-4 w-4 text-slate-400" /></a>)}
                      </div>
                    </div>
                  ))}
                  <div className="md:col-span-2 text-[10px] text-slate-400">created: {m.created_at}</div>
                </div>
              )}
            </div>
          );
        })}
        {(data.items || []).length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">لا توجد MIRs بعد.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PLACEHOLDER TABS for Iter-110b
// ─────────────────────────────────────────────────────────────────
function ComingSoonTab({ title, link, label, navigate }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <Activity className="mx-auto h-6 w-6 text-slate-400" />
      <div className="mt-2 text-sm font-bold text-slate-700">{title}</div>
      <p className="mt-1 text-xs text-slate-500">سيتم تفعيله بالكامل في Iter-110b.</p>
      {link && (
        <button onClick={() => navigate(link)} className="mt-3 inline-flex items-center gap-1 rounded-lg border border-violet-300 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-50">
          {label} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function MaterialsTab({ logs }) {
  // Aggregate materials_consumed across all daily logs.
  const map = {};
  (logs || []).forEach(l => {
    (l.materials_consumed || []).forEach(m => {
      const n = (m.name || "").trim() || "—";
      map[n] = (map[n] || 0) + Number(m.qty || 0);
    });
  });
  const rows = Object.entries(map).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="pec-tab-materials" className="space-y-3">
      <h3 className="text-sm font-bold text-slate-900">إجمالي المواد المستهلكة (من السجل اليومي)</h3>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          لم يتم تسجيل أي استهلاك مواد بعد. أضف &laquo;materials_consumed&raquo; داخل السجل اليومي ليظهر الإجمالي هنا.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          {rows.map(([name, qty], i) => (
            <div key={i} className={`flex items-center justify-between px-4 py-2 ${i ? "border-t border-slate-100" : ""}`}>
              <span className="text-sm font-semibold text-slate-900">{name}</span>
              <span className="text-sm text-violet-700 font-bold">{fmt(qty)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────
export default function ProjectExecutionCenter() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [areas, setAreas] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("site_readiness");
  const [refreshing, setRefreshing] = useState(false);

  const reloadAll = useCallback(async () => {
    if (!projectId) return;
    setRefreshing(true);
    try {
      const [dRes, aRes, lRes, tRes] = await Promise.all([
        axiosInstance.get(`/pec/${projectId}/dashboard`),
        axiosInstance.get(`/pec/areas?project_id=${projectId}`),
        axiosInstance.get(`/pec/daily-logs?project_id=${projectId}`),
        axiosInstance.get(`/pec/tasks?project_id=${projectId}&status=open`),
      ]);
      setDashboard(dRes.data);
      setAreas(aRes.data.items || []);
      setLogs(lRes.data.items || []);
      setTasks(tRes.data.items || []);
    } catch (e) {
      toast.error("تعذّر تحميل البيانات");
    } finally { setRefreshing(false); }
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!projectId) return;
      try {
        const [dRes, aRes, lRes, tRes] = await Promise.all([
          axiosInstance.get(`/pec/${projectId}/dashboard`),
          axiosInstance.get(`/pec/areas?project_id=${projectId}`),
          axiosInstance.get(`/pec/daily-logs?project_id=${projectId}`),
          axiosInstance.get(`/pec/tasks?project_id=${projectId}&status=open`),
        ]);
        if (!alive) return;
        setDashboard(dRes.data); setAreas(aRes.data.items || []); setLogs(lRes.data.items || []); setTasks(tRes.data.items || []);
      } catch (e) { if (alive) toast.error("تعذّر تحميل البيانات"); }
    })();
    return () => { alive = false; };
  }, [projectId]);

  if (!projectId) return <ProjectPicker />;
  if (!dashboard) {
    return (
      <div data-testid="pec-loading" className="p-8 text-center text-slate-500">
        <Activity className="mx-auto h-6 w-6 animate-pulse" />
        <div className="mt-2 text-sm">جارٍ تحضير مركز قيادة المشروع…</div>
      </div>
    );
  }

  const p = dashboard.project;
  const collectedPct = p.contract_value > 0 ? Math.round(p.total_collected / p.contract_value * 100) : 0;

  return (
    <div data-testid="project-execution-center" className="space-y-4 p-4 md:p-6">
      {/* HEADER */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-violet-50/40 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate("/project-execution")} className="mb-2 text-[11px] text-violet-600 hover:underline">‹ كل المشاريع</button>
            <div className="flex items-center gap-2">
              <Building className="h-6 w-6 text-violet-600" />
              <h1 data-testid="pec-project-name" className="text-2xl font-bold text-slate-900">{p.name}</h1>
              <button
                data-testid="pec-open-evidence"
                onClick={() => navigate(`/project-execution/${projectId}/evidence`)}
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-bold uppercase text-violet-700 hover:bg-violet-200"
                title="Evidence Timeline · Legal Proof Engine"
              >
                Evidence Timeline ›
              </button>
            </div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
              <span>العميل: <b className="text-slate-700">{p.client || "—"}</b></span>
              <span>عقد: <b className="text-slate-700">{p.contract_number || "—"}</b></span>
              <span>من {p.start_date} حتى {p.end_date}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div data-testid="pec-kpi-contract" className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">قيمة العقد</div>
              <div className="text-lg font-bold text-slate-900">{fmt(p.contract_value)} SAR</div>
            </div>
            <div data-testid="pec-kpi-completion" className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">الإنجاز</div>
              <div className="text-lg font-bold text-violet-700">{p.completion_pct}%</div>
            </div>
            <div data-testid="pec-kpi-invoiced" className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">المفوتر</div>
              <div className="text-lg font-bold text-amber-700">{fmt(p.total_invoiced)}</div>
            </div>
            <div data-testid="pec-kpi-collected" className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="text-[10px] text-slate-500 uppercase">المحصل</div>
              <div className="text-lg font-bold text-emerald-700">{fmt(p.total_collected)}</div>
              <div className="text-[10px] text-slate-400">{collectedPct}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* PHASE BAR */}
      <PhaseBar phases={dashboard.phases} />

      {/* TABS + SIDEBAR */}
      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div data-testid="pec-tabs" className="flex flex-nowrap gap-1 overflow-x-auto border-b border-slate-200 p-2 snap-x snap-mandatory scrollbar-thin md:flex-wrap md:overflow-visible">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`pec-tab-btn-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`inline-flex shrink-0 snap-start items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all min-h-[40px] ${active ? "bg-violet-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
            <button onClick={reloadAll} disabled={refreshing} className="mr-auto inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] text-slate-500 hover:text-violet-600">
              <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="p-4">
            {tab === "site_readiness" && <SiteReadinessTab projectId={projectId} onChanged={reloadAll} />}
            {tab === "areas"          && <AreasTab projectId={projectId} onChanged={reloadAll} />}
            {tab === "boq"            && <BoqTab projectId={projectId} onChanged={reloadAll} />}
            {tab === "contract"       && <ContractTab projectId={projectId} onChanged={reloadAll} />}
            {tab === "daily"          && <DailyLogTab projectId={projectId} areas={areas} onChanged={reloadAll} />}
            {tab === "mar"            && <MARTab projectId={projectId} areas={areas} onChanged={reloadAll} />}
            {tab === "mir"            && <MIRTab projectId={projectId} areas={areas} onChanged={reloadAll} />}
            {tab === "materials"      && <MaterialsTab logs={logs} />}
            {tab === "mobilization"   && <ComingSoonTab title="Mobilization Workflow" link={`/purchase-orders?project_id=${projectId}`} label="فتح أوامر الشراء للمشروع" navigate={navigate} />}
            {tab === "billing"        && <ComingSoonTab title="Billing Pipeline Trigger" link={`/progress-claims?project_id=${projectId}`} label="فتح Progress Claims للمشروع" navigate={navigate} />}
            {tab === "vo"             && <ComingSoonTab title="VO Workspace" link={`/variation-orders?project_id=${projectId}`} label="فتح Variation Orders للمشروع" navigate={navigate} />}
            {tab === "closeout"       && <ComingSoonTab title="Project Closeout" link="" label="" navigate={navigate} />}
          </div>
        </div>

        <DashboardSidebar data={dashboard} onTabClick={setTab} tasks={tasks} onTaskAction={async (t, status) => {
          try { await axiosInstance.patch(`/pec/tasks/${t.id}`, { status }); toast.success("تم تحديث المهمة"); reloadAll(); }
          catch { toast.error("فشل تحديث المهمة"); }
        }} />
      </div>

      {/* Iter-110b.5: Field Reality Layer — Floating quick actions */}
      <QuickActions projectId={projectId} areas={areas} onChanged={reloadAll} />
    </div>
  );
}
